import { constant } from "../constant";
import {
  type WeatherPayload,
  type WeatherVariable,
  WeatherVariableID,
  type WeatherVariableIDValue,
  type Keyframe,
} from "../types";

type WeatherData = {
  latest: WeatherPayload | null;
};

const data: WeatherData = {
  latest: null,
};

/**
 * Contains a source URL with a Unix timestamps in milliseconds
 */
export type SourceAndUnixTimestamp = {
  source: string;
  timestamp: number;
};

// biome-ignore lint/complexity/noStaticOnlyClass: we still want this static class
class WeatherDataHandler {
  static async fetchLatest(apiKey: string, maptilerSessionId: string) {
    const endpoint = `${constant.endpoint}?key=${apiKey}&mtsid=${maptilerSessionId}`;
    const res = await fetch(endpoint, { cache: "no-store" });
    data.latest = await res.json();
  }

  static getDataForWeatherVariableID(id: WeatherVariableIDValue): WeatherVariable | null {
    if (!data.latest) {
      return null;
    }

    const specificWeatherVariableData = data.latest.variables.filter(
      (wv: WeatherVariable) => wv.metadata.weather_variable.variable_id === id,
    );

    if (specificWeatherVariableData.length) {
      return specificWeatherVariableData[0];
    }

    return null;
  }

  static hasData() {
    return !!data.latest;
  }

  static getTemperatureData(): WeatherVariable | null {
    return WeatherDataHandler.getDataForWeatherVariableID(WeatherVariableID.GFS_TEMPERATURE_2M);
  }

  static getPressureData(): WeatherVariable | null {
    return WeatherDataHandler.getDataForWeatherVariableID(WeatherVariableID.GFS_PRESSURE_MSL);
  }

  static getPrecipitationData(): WeatherVariable | null {
    return WeatherDataHandler.getDataForWeatherVariableID(WeatherVariableID.GFS_PRECIPITATION_1H);
  }

  static getCloudCoverData(): WeatherVariable | null {
    return WeatherDataHandler.getDataForWeatherVariableID(WeatherVariableID.GFS_CLOUD_COVER_TOTAL);
  }

  static getFrozenPrecipitationData(): WeatherVariable | null {
    return WeatherDataHandler.getDataForWeatherVariableID(WeatherVariableID.GFS_FROZEN_PRECIPITATION_PERCENT);
  }

  static getRadarData(): WeatherVariable | null {
    return WeatherDataHandler.getDataForWeatherVariableID(WeatherVariableID.GFS_RADAR_COMPOSITE);
  }

  static getWindData(): WeatherVariable | null {
    return WeatherDataHandler.getDataForWeatherVariableID(WeatherVariableID.GFS_WIND_10M);
  }

  static getSourcesAndTimestamps(
    wv: WeatherVariable,
    apiKey: string,
    maptilerSessionId: string,
  ): Array<SourceAndUnixTimestamp> {
    return wv.keyframes.map((kf: Keyframe): SourceAndUnixTimestamp => {
      return {
        source: `${constant.tileSourceUrlSchema
          .replace("{tileset_id}", kf.id)
          .replace("{format}", wv.tile_format)}?key=${apiKey}&mtsid=${maptilerSessionId}`,
        timestamp: +new Date(kf.timestamp) / 1000, // timestamp is given in seconds
      };
    });
  }
}

export { WeatherDataHandler };
